import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  Snackbar,
  Autocomplete,
  TablePagination,
  Tooltip,
  Chip,
  FormHelperText,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import './JDDOMapping.css';
import { LoaderWrapper } from '../../components/index';
import { 
  getJDDOMappingList, 
  addJDDOMapping, 
  updateJDDOMapping, 
  deleteJDDOMapping,
  getJDList,
  getDOList
} from '../../services/api';

const JDDOMapping = () => {
  const [loading, setLoading] = useState(false);
  const [jdDoMappingList, setJDDoMappingList] = useState([]);
  const [jdList, setJDList] = useState([]);
  const [doList, setDOList] = useState([]);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Filter state
  const [filters, setFilters] = useState({
    jdName: '',
    doName: '',
    searchKeyword: '',
  });
  
  // Form state
  const [formData, setFormData] = useState({
    jdID: '',
    doID: '',
  });
  
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  
  // Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  
  // Form validation errors
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchJDDoMappingList();
    fetchJDList();
  }, []);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
  }, [filters]);

  // Fetch DO list when JD is selected
  useEffect(() => {
  
    if (formData.jdID) {
    
      fetchDOList(formData.jdID);
    } else {
     
      setDOList([]);
      setFormData(prev => ({ ...prev, doID: '' }));
    }
  }, [formData.jdID]);



  const fetchJDDoMappingList = async () => {
    try {
      setLoading(true);
      const response = await getJDDOMappingList({
        searchKeyword: filters.searchKeyword
      });
      if (response.data.success) {
        setJDDoMappingList(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching JD-DO mapping data:', error);
      showSnackbar('Error fetching JD-DO mapping data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchJDList = async () => {
    try {
      const response = await getJDList();
      if (response.data.success) {
        setJDList(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching JD list:', error);
      showSnackbar('Error fetching JD list', 'error');
    }
  };

  const fetchDOList = async (jdId) => {
    try {
   
      const response = await getDOList(jdId);
   
      if (response.data.success) {
     
        setDOList(response.data.data || []);
      
      } else {
        console.log('DO list fetch failed:', response.data.message);
      }
    } catch (error) {
      console.error('Error fetching DO list:', error);
      showSnackbar('Error fetching DO list', 'error');
    }
  };

  // Memoized filtered list
  const filteredJDDoMappingList = useMemo(() => {
    let filtered = jdDoMappingList;

    if (filters.jdName) {
      filtered = filtered.filter(item => 
        item.jdname && item.jdname.toLowerCase().includes(filters.jdName.toLowerCase())
      );
    }

    if (filters.doName) {
      filtered = filtered.filter(item => 
        item.Doname && item.Doname.toLowerCase().includes(filters.doName.toLowerCase())
      );
    }

    if (filters.searchKeyword) {
      filtered = filtered.filter(item => 
        (item.jdname && item.jdname.toLowerCase().includes(filters.searchKeyword.toLowerCase())) ||
        (item.Doname && item.Doname.toLowerCase().includes(filters.searchKeyword.toLowerCase()))
      );
    }

    return filtered;
  }, [jdDoMappingList, filters]);

  // Paginated list
  const paginatedJDDoMappingList = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredJDDoMappingList.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredJDDoMappingList, page, rowsPerPage]);

  const clearFilters = useCallback(() => {
    setFilters({
      jdName: '',
      doName: '',
      searchKeyword: '',
    });
    setPage(0);
  }, []);

  // Get unique values for filter dropdowns - memoized
  const uniqueJDNames = useMemo(() => {
    return [...new Set(jdDoMappingList.map(item => item.jdname).filter(Boolean))];
  }, [jdDoMappingList]);

  const uniqueDONames = useMemo(() => {
    return [...new Set(jdDoMappingList.map(item => item.Doname).filter(Boolean))];
  }, [jdDoMappingList]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.jdID) {
      newErrors.jdID = 'JD selection is required';
    }
    
    if (!formData.doID) {
      newErrors.doID = 'DO selection is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
  
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  }, [errors]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      
      const requestData = {
        jdID: formData.jdID,
        doID: formData.doID,
      };
      
      const response = await addJDDOMapping(requestData);
      
      if (response.data.success) {
        showSnackbar('JD-DO mapping added successfully', 'success');
        resetForm();
        fetchJDDoMappingList();
        setPage(0);
      } else {
        showSnackbar(response.data.message || 'Error adding JD-DO mapping', 'error');
      }
    } catch (error) {
      console.error('Error adding JD-DO mapping:', error);
      const errorMessage = error.response?.data?.message || 'Error adding JD-DO mapping';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  }, [formData]);

  const handleUpdate = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      
      const requestData = {
        id: editId,
        jdID: formData.jdID,
        doID: formData.doID,
      };
      
      const response = await updateJDDOMapping(requestData);
      
      if (response.data.success) {
        showSnackbar('JD-DO mapping updated successfully', 'success');
        resetForm();
        fetchJDDoMappingList();
      } else {
        showSnackbar(response.data.message || 'Error updating JD-DO mapping', 'error');
      }
    } catch (error) {
      console.error('Error updating JD-DO mapping:', error);
      const errorMessage = error.response?.data?.message || 'Error updating JD-DO mapping';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  }, [formData, editId]);

  const handleEdit = useCallback(async (item) => {
  
    // We need to find the JD and DO IDs from the names
    // Find JD ID by name
    const selectedJD = jdList.find(jd => jd.LoginName === item.jdname);
    const selectedDO = doList.find(do_officer => (do_officer.loginname || do_officer.LoginName) === item.Doname);
  
    setFormData({
      jdID: selectedJD?.OrgId || '',
      doID: selectedDO?.OrgId || '',
    });
    setEditMode(true);
    setEditId(item.id);
    
    // Fetch DO list for the selected JD if we found the JD
    if (selectedJD?.OrgId) {
      await fetchDOList(selectedJD.OrgId);
    }
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [jdList, doList]);

  const handleDeleteClick = useCallback((id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      setLoading(true);
      const response = await deleteJDDOMapping(deleteId);
      
      if (response.data.success) {
        showSnackbar('JD-DO mapping deleted successfully', 'success');
        fetchJDDoMappingList();
      } else {
        showSnackbar(response.data.message || 'Error deleting JD-DO mapping', 'error');
      }
    } catch (error) {
      console.error('Error deleting JD-DO mapping:', error);
      const errorMessage = error.response?.data?.message || 'Error deleting JD-DO mapping';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setDeleteId(null);
    }
  }, [deleteId]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setDeleteId(null);
  }, []);

  const resetForm = useCallback(() => {
    setFormData({
      jdID: '',
      doID: '',
    });
    setEditMode(false);
    setEditId(null);
    setErrors({});
    setDOList([]);
  }, []);

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  // Pagination handlers
  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  // Handle search
  const handleSearchChange = useCallback((e) => {
    setFilters(prev => ({ ...prev, searchKeyword: e.target.value }));
  }, []);

  if (loading) {
    return <LoaderWrapper loading={true} type="page" message="Loading..." />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#00024c' }}>
        JD and DO Mapping
      </Typography>

      {/* Form Section */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <form onSubmit={editMode ? handleUpdate : handleSubmit}>
          <Grid container spacing={3}>
            {/* Joint Director List */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                options={jdList}
                getOptionLabel={(option) => option.LoginName || ''}
                value={jdList.find(jd => jd.OrgId === formData.jdID) || null}
                onChange={(event, newValue) => {
                  const syntheticEvent = {
                    target: {
                      name: 'jdID',
                      value: newValue ? newValue.OrgId : ''
                    }
                  };
                  handleInputChange(syntheticEvent);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Joint Director List"
                    required
                    error={!!errors.jdID}
                    helperText={errors.jdID}
                    placeholder="Type to search Joint Director..."
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {option.LoginName}
                      </Typography>
                      {/* <Typography variant="caption" color="text.secondary">
                        ID: {option.OrgId}
                      </Typography> */}
                    </Box>
                  </Box>
                )}
                noOptionsText="No Joint Directors found"
                clearOnEscape
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
              />
            </Grid>

            {/* Dealing Officer List */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                options={doList}
                getOptionLabel={(option) => option.loginname || option.LoginName || ''}
                value={doList.find(do_officer => do_officer.OrgId === formData.doID) || null}
                onChange={(event, newValue) => {
                  const syntheticEvent = {
                    target: {
                      name: 'doID',
                      value: newValue ? newValue.OrgId : ''
                    }
                  };
                  handleInputChange(syntheticEvent);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Dealing Officer List"
                    required
                    error={!!errors.doID}
                    helperText={errors.doID}
                    placeholder="Type to search Dealing Officer..."
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {option.loginname || option.LoginName}
                      </Typography>
                      {/* <Typography variant="caption" color="text.secondary">
                        ID: {option.OrgId}
                      </Typography> */}
                    </Box>
                  </Box>
                )}
                disabled={!formData.jdID || doList.length === 0}
                noOptionsText={!formData.jdID ? "Please select a Joint Director first" : "No Dealing Officers found"}
                clearOnEscape
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
              />
            </Grid>

            {/* Buttons */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color={editMode ? 'error' : 'primary'}
                  disabled={loading}
                >
                  {editMode ? 'Update' : 'Submit'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={resetForm}
                  disabled={loading}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Table Section */}
      <Paper sx={{ width: '100%' }}>
        <Box sx={{ 
          p: 2, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          backgroundColor: '#e0ebeb',
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            JD-DO Mapping Management
          </Typography>
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchJDDoMappingList}
            size="small"
            variant="outlined"
          >
            Refresh
          </Button>
        </Box>

        {/* Filter Section */}
        <Box sx={{ p: 2, backgroundColor: '#f9f9f9', borderBottom: '1px solid #e0e0e0' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <FilterListIcon sx={{ mr: 1, color: '#666' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#666' }}>
                  Filter JD-DO Mappings
                </Typography>
              </Box>
            </Grid>
            
            {/* Search Field */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                size="small"
                label="Keyword Search"
                placeholder="Search by JD or DO name..."
                value={filters.searchKeyword}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: '#666' }} />
                }}
                fullWidth
              />
            </Grid>

            {/* JD Name Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                size="small"
                options={uniqueJDNames}
                value={filters.jdName || null}
                onChange={(event, newValue) => {
                  setFilters(prev => ({ ...prev, jdName: newValue || '' }));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="JD Name" placeholder="Search" />
                )}
                clearOnEscape
              />
            </Grid>

            {/* DO Name Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                size="small"
                options={uniqueDONames}
                value={filters.doName || null}
                onChange={(event, newValue) => {
                  setFilters(prev => ({ ...prev, doName: newValue || '' }));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="DO Name" placeholder="Search" />
                )}
                clearOnEscape
              />
            </Grid>

            {/* Clear Filters Button */}
            <Grid item xs={12} sm={6} md={2}>
              <Button
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                size="small"
                variant="outlined"
                color="secondary"
                fullWidth
              >
                Clear Filters
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="caption" sx={{ color: '#666' }}>
                Showing {filteredJDDoMappingList.length} of {jdDoMappingList.length} items
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 100 }}>Actions</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 80 }}>Sr No.</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 200 }}>JD Name</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 200 }}>DO Name</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredJDDoMappingList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {jdDoMappingList.length === 0 ? 'No JD-DO mappings found' : 'No items match the selected filters'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedJDDoMappingList.map((item, index) => (
                  <TableRow
                    key={item.id}
                    hover
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleEdit(item)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDeleteClick(item.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      {page * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={item.jdname || 'N/A'} variant='outlined' size='small' color="primary" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={item.Doname || 'N/A'} variant='outlined' size='small' color="secondary" />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredJDDoMappingList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Items per page:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
          }
        />
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this JD-DO mapping?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default JDDOMapping;